import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { AuthLayout, FormError } from '@/components/auth/AuthLayout';
import HumanCheck, { HumanCheckRef, needsHumanCheck } from '@/components/auth/HumanCheck';
import PasswordField from '@/components/auth/PasswordField';

const loginSchema = z.object({
  email: z.string().email('Enter a valid email address'),
  password: z.string().min(1, 'Enter your password'),
});

type LoginFormData = z.infer<typeof loginSchema>;


const LoginPage: React.FC = () => {
  const { login, isLoading } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  // The button stays off until the invisible browser check hands back a token.
  const waitingForCheck = needsHumanCheck && !token;
  const check = useRef<HumanCheckRef>(null);

  const { register, handleSubmit, formState: { errors } } = useForm<LoginFormData>({ resolver: zodResolver(loginSchema) });

  const onSubmit = async (data: LoginFormData) => {
    setError(null);
    if (waitingForCheck) {
      setError('The browser check is still running. Try again in a moment.');
      return;
    }
    try {
      await login(data.email, data.password, token ?? '');
      // navigation handled by the useEffect watching isAuthenticated
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed. Try again.');
      check.current?.reset(); // a token works once
    }
  };

  return (
    <AuthLayout title="Sign in" subtitle="Manage API keys, usage and billing for your Latlens account.">
      <form className="space-y-5" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Input
          label="Email address"
          type="email"
          autoComplete="email"
          leftIcon={<Mail className="h-4 w-4" />}
          error={errors.email?.message}
          {...register('email')}
        />
        <div>
          <PasswordField label="Password" autoComplete="current-password" error={errors.password?.message} {...register('password')} />
          <div className="mt-2 text-right">
            <Link to="/forgot-password" className="text-sm font-medium text-accent-text hover:underline">
              Forgot your password?
            </Link>
          </div>
        </div>
        <HumanCheck ref={check} onToken={setToken} />
        <FormError message={error} />
        <Button type="submit" size="lg" loading={isLoading} disabled={waitingForCheck} className="w-full">
          Sign in
        </Button>
        <p className="text-center text-sm text-muted-foreground">
          New to Latlens?{' '}
          <Link to="/register" className="font-medium text-accent-text hover:underline">Create an account</Link>
        </p>
      </form>
    </AuthLayout>
  );
};

export default LoginPage;
